/**
 * API client. Talks only to customer-safe endpoints.
 * Access token is held in memory + sessionStorage (survives reload, not a new tab).
 */
import type { CustomerEstimate, IntakeOptions } from './types';

const TOKEN_KEY = 'pb.accessToken';
const ID_TOKEN_KEY = 'pb.idToken';
const ROLE_KEY = 'pb.role';

export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
}
export function getIdToken(): string | null {
  return sessionStorage.getItem(ID_TOKEN_KEY);
}
export function setIdToken(token: string): void {
  sessionStorage.setItem(ID_TOKEN_KEY, token);
}
export function clearToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(ID_TOKEN_KEY);
  clearRoleCache();
}

/**
 * API origin. Empty in local dev so requests stay same-origin and go through the
 * Vite proxy; set to the deployed backend's URL (VITE_API_URL) in production.
 * Trailing slash trimmed so `${API_BASE}${path}` is always well-formed.
 */
const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (res.status === 401) {
    clearToken();
    throw new ApiError('Session expired', 401);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(body.error ?? 'Request failed', res.status);
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// --- auth ----------------------------------------------------------------

export interface RequestOtpResponse {
  session: string;
  destination?: string;
}

export function requestOtp(email: string): Promise<RequestOtpResponse> {
  return req<RequestOtpResponse>('/auth/request-otp', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export interface VerifyOtpResponse {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  expiresIn?: number;
}

export async function verifyOtp(
  email: string,
  code: string,
  session: string,
): Promise<VerifyOtpResponse> {
  const tokens = await req<VerifyOtpResponse>('/auth/verify-otp', {
    method: 'POST',
    body: JSON.stringify({ email, code, session }),
  });
  setToken(tokens.accessToken);
  // Keep the id token for the one-time contractor claim (carries the verified
  // email, which the access token does not). Cleared on logout.
  setIdToken(tokens.idToken);
  return tokens;
}

// --- estimates (customer-safe) -------------------------------------------

// The answer set the engine expects. Kept loose here; the server validates it.
export interface AnswerSetPayload {
  service_line: string;
  product_code: string;
  triggers: Array<{ service_line: string; module_code: string; quantity: number }>;
  tileTierCode?: string;
  activeModifierCodes: string[];
  unsureQuestionCodes: string[];
  unsureOptions: Record<string, Array<Array<{ service_line: string; module_code: string; quantity: number }>>>;
  sessionVars: Record<string, number>;
}

export function createCustomerEstimate(answers: AnswerSetPayload): Promise<CustomerEstimate> {
  return req<CustomerEstimate>('/customer/estimates', {
    method: 'POST',
    body: JSON.stringify(answers),
  });
}

export function getCustomerEstimate(id: string): Promise<CustomerEstimate> {
  return req<CustomerEstimate>(`/customer/estimates/${id}`);
}

// --- questions (customer-safe, grouped into screens) ---------------------

export interface CustomerOption {
  code: string;
  label: string | null;
  sublabel: string | null;
  isUnsure: boolean;
  requiresNumber: boolean;
  defaultNumber: number | null;
  opens: string | null;
}

export interface CustomerQuestion {
  code: string;
  inputType: string; // SINGLE | MULTI | NUMBER | TEXT
  prompt: string;
  helper: string | null;
  isGating: boolean;
  parentQuestionCode: string | null;
  parentOptionCode: string | null;
  options: CustomerOption[];
}

export interface CustomerQuestionScreen {
  screenId: string;
  prompt: string;
  helper: string | null;
  questions: CustomerQuestion[];
}

export function getQuestions(serviceLine: string): Promise<{ serviceLine: string; screens: CustomerQuestionScreen[] }> {
  return req(`/customer/questions/${serviceLine}`);
}

// --- running estimate: session snapshot + preview ------------------------

/** Pins a reference snapshot for the flow session; returns its id. */
export async function pinSnapshot(): Promise<string> {
  const { snapshotId } = await req<{ snapshotId: string }>('/customer/snapshot', {
    method: 'POST',
  });
  return snapshotId;
}

/**
 * Previews the scope for a (possibly partial) answer set against the pinned
 * snapshot. Persists nothing. Throws ApiError(409) if the snapshot expired.
 */
export function previewEstimate(
  answers: AnswerSetPayload,
  snapshotId: string,
): Promise<CustomerEstimate> {
  return req<CustomerEstimate>('/customer/estimates/preview', {
    method: 'POST',
    body: JSON.stringify({ ...answers, snapshotId }),
  });
}

/** A raw customer answer — option selections + numbers, resolved server-side. */
export interface RawAnswer {
  questionCode: string;
  selectedOptionCodes?: string[];
  numberByOption?: Record<string, number>;
  numberValue?: number;
  textValue?: string;
}

// --- save & resume -------------------------------------------------------

export interface SavedSession {
  serviceLine: string;
  productCode: string;
  answers: RawAnswer[];
  currentScreenIndex: number;
  projectId?: string | null;
  roomIndex?: number | null;
  updatedAt: string;
}

/** Autosave the in-progress design session. 204 on success. */
export async function saveSession(input: {
  serviceLine: string;
  productCode: string;
  answers: RawAnswer[];
  currentScreenIndex: number;
  projectId?: string;
  roomIndex?: number;
}): Promise<void> {
  await req<void>('/customer/sessions', { method: 'PUT', body: JSON.stringify(input) });
}

/** Load a saved session to resume, or null if none exists. */
export async function loadSession(
  serviceLine: string,
  key?: { projectId?: string; roomIndex?: number },
): Promise<SavedSession | null> {
  const qs = new URLSearchParams();
  if (key?.projectId) qs.set('projectId', key.projectId);
  if (key?.roomIndex != null) qs.set('roomIndex', String(key.roomIndex));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  try {
    return await req<SavedSession>(`/customer/sessions/${serviceLine}${suffix}`);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

// --- intake options (end-of-flow budget + timeline questions) ------------

/** Reads the budget and timeline option lists for the free-submit questions. */
export function getIntakeOptions(): Promise<IntakeOptions> {
  return req<IntakeOptions>('/customer/intake-options');
}

// --- projects (a visit that groups the rooms into one free estimate) ------

export interface ProjectRoomView {
  id: string;
  label: string | null;
  status: string;
  serviceLine?: string;
}

export interface ProjectView {
  id: string;
  name?: string | null;
  status: string;
  serviceLine?: string | null;
  /** The sent total (integer cents), present only once the estimate is SENT. */
  sentTotalCents?: number;
  rooms: ProjectRoomView[];
}

/** Starts a project (visit) under a master, with a customer-chosen name. Returns its id. */
export async function createProject(input: { masterId?: string; name?: string } = {}): Promise<string> {
  const { projectId } = await req<{ projectId: string; status: string }>('/customer/projects', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return projectId;
}

/** Reads a project + its rooms for the summary screen. */
export function getProject(id: string): Promise<ProjectView> {
  return req<ProjectView>(`/customer/projects/${id}`);
}

/**
 * Submits the whole project for a free estimate, with optional budget/timeline
 * lead-scoring answers. Returns { status: 'SUBMITTED' } — there is no checkout.
 */
export function submitProject(
  id: string,
  body: { budgetRangeCode?: string; timelineCode?: string },
): Promise<{ status: string }> {
  return req<{ status: string }>(`/customer/projects/${id}/submit`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Resolving preview — sends raw option selections; the server resolves them to
 * triggers and returns the price-free scope (items + messages). This is the
 * data-driven client's preview path.
 */
export function previewAnswers(input: {
  snapshotId: string;
  serviceLine: string;
  productCode: string;
  answers: RawAnswer[];
  appliancesUndecided?: boolean;
}): Promise<CustomerEstimate> {
  return req<CustomerEstimate>('/customer/preview', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Create — resolves raw answers server-side, runs the engine, PERSISTS the
 * estimate, and returns the price-free customer scope view (the S9 close).
 */
export function createEstimateFromAnswers(input: {
  snapshotId: string;
  serviceLine: string;
  productCode: string;
  answers: RawAnswer[];
  appliancesUndecided?: boolean;
  /** Attach this room to a project (the multi-room loop). */
  projectId?: string;
  /** Customer-supplied room name, e.g. "Master bath". */
  label?: string;
}): Promise<CustomerEstimate & { id: string }> {
  return req<CustomerEstimate & { id: string }>('/customer/estimates/from-answers', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// --- identity / role -----------------------------------------------------

export interface MeResponse {
  userId: string;
  role: 'ADMINISTRATOR' | 'ESTIMATOR' | 'CLIENT' | 'CONTRACTOR';
}

export function getMe(): Promise<MeResponse> {
  return req<MeResponse>('/me');
}

export type Role = MeResponse['role'];

// Role cache: /me is called on nearly every screen (and the global nav). Cache
// the result for the session so we don't refetch on every navigation. Cleared
// on logout (clearToken) and when the token changes.
let rolePromise: Promise<Role | null> | null = null;

/** The cached role, if we've already resolved it this session (sync read). */
export function cachedRole(): Role | null {
  return (sessionStorage.getItem(ROLE_KEY) as Role | null) ?? null;
}

/**
 * Resolves the current user's role, fetching /me once and memoizing it. Returns
 * null when signed out or on error (so callers can treat it as "no session").
 */
export function getRole(): Promise<Role | null> {
  if (!getToken()) return Promise.resolve(null);
  const cached = cachedRole();
  if (cached) return Promise.resolve(cached);
  if (!rolePromise) {
    rolePromise = getMe()
      .then((me) => {
        sessionStorage.setItem(ROLE_KEY, me.role);
        return me.role;
      })
      .catch(() => null)
      .finally(() => {
        rolePromise = null;
      });
  }
  return rolePromise;
}

export function clearRoleCache(): void {
  sessionStorage.removeItem(ROLE_KEY);
  rolePromise = null;
}

// --- intake / masters ----------------------------------------------------

export interface Referrer {
  id: number;
  name: string;
}

export interface Master {
  id: string;
  fullName: string;
  phone?: string;
  email?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  referralSource?: string;
  referralDetail?: string;
}

export interface MasterInput {
  fullName: string;
  phone?: string;
  email?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  referralSource?: string;
  referralDetail?: string;
  /** Confirm creating a new record at a street where the caller already has one. */
  confirmNewAtStreet?: boolean;
}

/** A possible-duplicate match returned when the caller already has a record at the street. */
export interface MasterStreetMatch {
  id: string;
  fullName: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string;
  state: string;
  postalCode: string;
}

export type CreateMasterResult =
  | { master: Master; created: boolean; possibleDuplicate?: false }
  | { possibleDuplicate: true; matches: MasterStreetMatch[] };

export async function getReferrers(): Promise<Referrer[]> {
  const { referrers } = await req<{ referrers: Referrer[] }>('/customer/referrers');
  return referrers;
}

export async function getMasters(): Promise<Master[]> {
  const { masters } = await req<{ masters: Master[] }>('/customer/masters');
  return masters;
}

export function createMaster(input: MasterInput): Promise<CreateMasterResult> {
  return req<CreateMasterResult>('/customer/masters', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

// --- dashboard -----------------------------------------------------------

export interface DashboardProject {
  id: string;
  name: string | null;
  status: string;
  /**
   * Still present in the staff dashboard payload for internal use. NEVER
   * rendered to the customer — the customer dashboard shows status only.
   */
  totalPriceCents?: number;
}

export interface DashboardMaster {
  id: string;
  fullName: string;
  addressLine1: string;
  city: string;
  state: string;
  postalCode: string;
  projects: DashboardProject[];
}

export async function getDashboard(): Promise<DashboardMaster[]> {
  const { masters } = await req<{ masters: DashboardMaster[] }>('/customer/dashboard');
  return masters;
}

// --- project start (with a master) ---------------------------------------

export async function startProject(masterId?: string): Promise<{ projectId: string; name: string | null }> {
  return req<{ projectId: string; status: string; name: string | null }>('/customer/projects', {
    method: 'POST',
    body: JSON.stringify(masterId ? { masterId } : {}),
  });
}

// --- session cancel (discard a draft) ------------------------------------

export async function cancelSession(
  serviceLine: string,
  key?: { projectId?: string; roomIndex?: number },
): Promise<void> {
  const qs = new URLSearchParams();
  if (key?.projectId) qs.set('projectId', key.projectId);
  if (key?.roomIndex != null) qs.set('roomIndex', String(key.roomIndex));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  await req<void>(`/customer/sessions/${serviceLine}${suffix}`, { method: 'DELETE' });
}

// --- admin (Administrator / Estimator) -----------------------------------

export async function adminSearchMasters(query: string): Promise<Master[]> {
  const { masters } = await req<{ masters: Master[] }>(
    `/admin/masters?q=${encodeURIComponent(query)}`,
  );
  return masters;
}

// --- staff lead queue + estimate editing ---------------------------------

export interface Lead {
  projectId: string;
  projectName: string | null;
  masterId: string | null;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  /** 1–5, or null for UNSCORED. */
  leadScore: number | null;
  budgetLabel: string | null;
  timelineLabel: string | null;
  /** Internal engine total (staff only), integer cents. */
  engineTotalCents: number;
  submittedAt: string | null;
}

export async function getLeads(): Promise<Lead[]> {
  const { leads } = await req<{ leads: Lead[] }>('/admin/lead-queue');
  return leads;
}

// --- staff dashboard (projects by month + status) ------------------------

export interface DashboardStaffProject {
  projectId: string;
  projectName: string | null;
  customerName: string | null;
  /** OPEN | SUBMITTED | SENT | ACCEPTED | CANCELLED */
  status: string;
  /** True once the design/site-visit fee is paid. */
  paid: boolean;
  leadScore: number | null;
  budgetLabel: string | null;
  timelineLabel: string | null;
  /** Internal total, integer cents. */
  totalCents: number;
  submittedAt: string | null;
  createdAt: string;
}

export async function getAdminDashboard(): Promise<DashboardStaffProject[]> {
  // /admin/pipeline (not /admin/dashboard — that's the SPA page route).
  const { projects } = await req<{ projects: DashboardStaffProject[] }>('/admin/pipeline');
  return projects;
}

export interface AdminEstimateLine {
  moduleServiceLine: string;
  moduleCode: string;
  name: string;
  quantity: number;
  laborCostCents: number | null;
  lineCostCents: number | null;
  linePriceCents: number | null;
  extraFeeCents: number | null;
}

export interface AdminRoom {
  estimateId: string;
  label: string | null;
  status: string;
  serviceLine: string;
  engineTotalCents: number | null;
  sentTotalCents: number | null;
  answers: Array<{ label: string; value: string }>;
  /** The customer's actual Q&A (what they told us), read-only. */
  answerLabels: Array<{ prompt: string; answers: string[] }>;
  lines: AdminEstimateLine[];
}

export interface AdminProject {
  projectId: string;
  name: string | null;
  status: string;
  leadScore: number | null;
  budgetLabel: string | null;
  timelineLabel: string | null;
  rooms: AdminRoom[];
}

export function getAdminProject(projectId: string): Promise<AdminProject> {
  return req<AdminProject>(`/admin/projects/${projectId}`);
}

export interface LineMultiplier {
  moduleServiceLine: string;
  moduleCode: string;
  multiplier: number;
}

export function editEstimate(
  estimateId: string,
  body: { reason?: string; lineMultipliers: LineMultiplier[] },
): Promise<{ id: string; status: string; engineTotalCents: number | null; sentTotalCents: number | null }> {
  return req(`/admin/estimates/${estimateId}/edit`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Sends the project's estimate to the customer WITHOUT requiring a price edit.
 * Marks every sendable room SENT, flips the project to SENT, and emails the
 * customer their total. Use after optionally applying edits. Idempotent.
 */
export function sendEstimate(
  projectId: string,
): Promise<{ status: string; totalCents: number; emailed: boolean }> {
  return req(`/admin/projects/${projectId}/send`, { method: 'POST' });
}

// --- contractors (staff-managed) -----------------------------------------

export interface Contractor {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  trade: string | null;
  notes: string | null;
  active: boolean;
}

export async function getContractors(activeOnly = false): Promise<Contractor[]> {
  const qs = activeOnly ? '?activeOnly=true' : '';
  const { contractors } = await req<{ contractors: Contractor[] }>(`/admin/contractors${qs}`);
  return contractors;
}

export function createContractor(input: {
  name: string;
  email?: string;
  phone?: string;
  trade?: string;
  notes?: string;
}): Promise<{ contractor: Contractor }> {
  return req('/admin/contractors', { method: 'POST', body: JSON.stringify(input) });
}

export function updateContractor(
  id: string,
  input: Partial<{ name: string; email: string | null; phone: string | null; trade: string | null; notes: string | null; active: boolean }>,
): Promise<{ contractor: Contractor }> {
  return req(`/admin/contractors/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

// --- contractor bids (staff) ---------------------------------------------

export interface ProjectBid {
  id: string;
  projectId: string;
  contractorId: string;
  contractorName: string;
  status: string;
  offeredAmountCents: number;
  counterAmountCents: number | null;
  acceptedAmountCents: number | null;
  expiresAt: string;
  sentAt: string;
  /** Gross ($) and margin (0–1) if this bid's amount were the subcontract cost. */
  grossCents: number;
  marginPct: number;
}

export interface ProjectBidsView {
  projectId: string;
  status: string;
  laborCents: number;
  materialsCents: number;
  priceCents: number;
  suggestedOfferCents: number;
  customerAccepted: boolean;
  bids: ProjectBid[];
}

export function getProjectBids(projectId: string): Promise<ProjectBidsView> {
  return req<ProjectBidsView>(`/admin/projects/${projectId}/bids`);
}

export function sendProjectBids(
  projectId: string,
  bids: Array<{ contractorId: string; offeredAmountCents?: number }>,
): Promise<{ created: number }> {
  return req(`/admin/projects/${projectId}/bids`, {
    method: 'POST',
    body: JSON.stringify({ bids }),
  });
}

export function assignBid(projectId: string, bidId: string): Promise<{ status: string; acceptedAmountCents: number }> {
  return req(`/admin/projects/${projectId}/bids/${bidId}/assign`, { method: 'POST' });
}

export function rejectCounter(projectId: string, bidId: string): Promise<{ status: string }> {
  return req(`/admin/projects/${projectId}/bids/${bidId}/reject-counter`, { method: 'POST' });
}

// --- public contractor bid response (no login, token-keyed) --------------

export interface BidView {
  status: string;
  contractorName: string;
  projectName: string | null;
  roomLabels: string[];
  offeredAmountCents: number;
  counterAmountCents: number | null;
  expiresAt: string;
  canRespond: boolean;
}

export function getBid(token: string): Promise<BidView> {
  return req<BidView>(`/bids/${token}`);
}

export function acceptBid(token: string): Promise<{ status: string }> {
  return req(`/bids/${token}/accept`, { method: 'POST' });
}

export function counterBid(token: string, counterAmountCents: number): Promise<{ status: string }> {
  return req(`/bids/${token}/counter`, { method: 'POST', body: JSON.stringify({ counterAmountCents }) });
}

export function declineBid(token: string): Promise<{ status: string }> {
  return req(`/bids/${token}/decline`, { method: 'POST' });
}

// --- contractor portal (logged-in contractor) ----------------------------

/**
 * One-time link of this login to a contractor row by the id-token email, then
 * grants the CONTRACTOR role. Call once right after sign-in. Returns whether the
 * email matched a contractor. Safe to call for any user (non-matches = linked:false).
 */
export function claimContractor(idToken: string): Promise<{ linked: boolean; contractorId?: string }> {
  return req('/contractor/claim', { method: 'POST', body: JSON.stringify({ idToken }) });
}

export interface ContractorBid {
  id: string;
  projectName: string | null;
  roomLabels: string[];
  status: string;
  offeredAmountCents: number;
  counterAmountCents: number | null;
  acceptedAmountCents: number | null;
  expiresAt: string;
  sentAt: string;
  canRespond: boolean;
}

export async function getContractorBids(): Promise<ContractorBid[]> {
  const { bids } = await req<{ bids: ContractorBid[] }>('/contractor/bids');
  return bids;
}

export function acceptMyBid(bidId: string): Promise<{ status: string }> {
  return req(`/contractor/bids/${bidId}/accept`, { method: 'POST' });
}

export function counterMyBid(bidId: string, counterAmountCents: number): Promise<{ status: string }> {
  return req(`/contractor/bids/${bidId}/counter`, {
    method: 'POST',
    body: JSON.stringify({ counterAmountCents }),
  });
}

export function declineMyBid(bidId: string): Promise<{ status: string }> {
  return req(`/contractor/bids/${bidId}/decline`, { method: 'POST' });
}

export interface ContractorProjectScopeItem {
  name: string;
  quantity: number;
}
export interface ContractorProjectRoom {
  label: string;
  serviceLine: string;
  scope: ContractorProjectScopeItem[];
}
export interface ContractorProjectMedia {
  kind: 'image' | 'video' | 'link';
  url: string | null;
  label?: string | null;
}
export interface ContractorProjectDetails {
  projectName: string | null;
  rooms: ContractorProjectRoom[];
  media: ContractorProjectMedia[];
  /** False until photo/video capture (S3) is built — then media may be populated. */
  mediaAvailable: boolean;
}

/** Scope-of-work detail for a bid's project (no prices). For the contractor's assessment. */
export function getContractorBidProject(bidId: string): Promise<ContractorProjectDetails> {
  return req<ContractorProjectDetails>(`/contractor/bids/${bidId}/project`);
}

// --- project media (photos / videos / reference links) -------------------

export interface ProjectMediaItem {
  id: string;
  kind: 'image' | 'video' | 'link';
  label: string | null;
  contentType?: string | null;
  /** Presigned GET URL (files) or the external URL (links). */
  url: string | null;
  createdAt: string;
}

export async function listProjectMedia(projectId: string): Promise<ProjectMediaItem[]> {
  const { media } = await req<{ media: ProjectMediaItem[] }>(`/customer/projects/${projectId}/media`);
  return media;
}

export function addProjectLink(
  projectId: string,
  url: string,
  label?: string,
): Promise<ProjectMediaItem> {
  return req(`/customer/projects/${projectId}/media/link`, {
    method: 'POST',
    body: JSON.stringify(label ? { url, label } : { url }),
  });
}

export function deleteProjectMedia(projectId: string, mediaId: string): Promise<{ deleted: boolean }> {
  return req(`/customer/projects/${projectId}/media/${mediaId}`, { method: 'DELETE' });
}

/**
 * Uploads one file to a project: asks for a presigned URL, PUTs the bytes
 * straight to S3 (not through the API), then confirms so the row is written.
 * onProgress reports 0..1. Returns the created media item.
 */
export async function uploadProjectMedia(
  projectId: string,
  file: File,
  opts: { label?: string; onProgress?: (fraction: number) => void } = {},
): Promise<ProjectMediaItem> {
  const kind: 'image' | 'video' = file.type.startsWith('video/') ? 'video' : 'image';
  const { mediaId, uploadUrl } = await req<{ mediaId: string; key: string; uploadUrl: string }>(
    `/customer/projects/${projectId}/media/upload-url`,
    {
      method: 'POST',
      body: JSON.stringify({ filename: file.name, contentType: file.type, sizeBytes: file.size }),
    },
  );

  // Direct-to-S3 PUT with progress via XHR (fetch has no upload progress).
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && opts.onProgress) opts.onProgress(e.loaded / e.total);
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });

  return req<ProjectMediaItem>(`/customer/projects/${projectId}/media/confirm`, {
    method: 'POST',
    body: JSON.stringify({
      mediaId,
      kind,
      contentType: file.type,
      sizeBytes: file.size,
      ...(opts.label ? { label: opts.label } : {}),
    }),
  });
}
